import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '@/modules/agents/api/agentsApi';
import { agentsKeys } from '@/modules/agents/queryKeys';
import { PageHeader } from '@/shared/ui/PageHeader';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Button } from '@/shared/ui/Button';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import type { PartnerRow, Pick, PicksResponse, PlanTouchInput } from '../api/communityApi';
import { communityKeys } from '../queryKeys';
import {
  useCommunityActions,
  useCommunityNetwork,
  useCommunityPicks,
  useCommunityState,
} from '../hooks/useCommunity';
import { JoePanel } from '../components/JoePanel';
import { NetworkTable } from '../components/NetworkTable';
import { PartnerDetail } from '../components/PartnerDetail';
import { PickCard } from '../components/PickCard';
import { ReservePicks } from '../components/ReservePicks';
import { PlanTouchModal, type PlanTarget } from '../components/PlanTouchModal';
import styles from '../components/Community.module.css';

type Tab = 'picks' | 'network';

/**
 * The Community page: Joe's picks (while Joe is active) and My Network (always, once the feature
 * is on). The server 404s everything here without the practice flag, so the route only renders
 * behind FeatureRoute and the page never has to guess.
 */
export function CommunityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = searchParams.get('tab') === 'network' ? 'network' : 'picks';
  const selectedPartner = searchParams.get('partner');
  const toast = useToast();
  const queryClient = useQueryClient();
  const {
    data: state,
    isLoading: stateLoading,
    isError: stateError,
    refetch: refetchState,
  } = useCommunityState();
  const { data: picks, isLoading: picksLoading, isError: picksError } = useCommunityPicks();
  const { data: network, isLoading: networkLoading } = useCommunityNetwork();
  const actions = useCommunityActions();
  const [target, setTarget] = useState<PlanTarget | null>(null);
  const [learned, setLearned] = useState<{ message: string; ruleId: string | null } | null>(null);
  // Joe's ask after a pattern of dismissals (drop a category, shrink the radius): stays until answered.
  const [followUp, setFollowUp] = useState<{
    kind: 'drop_category' | 'shrink_radius';
    detail: string;
    category?: string;
  } | null>(null);

  const setParam = (key: string, value: string | null) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null) next.delete(key);
      else next.set(key, value);
      return next;
    });

  // Joe's open picks report is where the owner talks to him ("other options", "why this one"):
  // the panel links straight to its thread, or to Tasks when no report is open this month.
  const { data: joeTasks } = useQuery({
    queryKey: agentsKeys.tasks(actions.locationId),
    queryFn: () => agentsApi.listTasks(actions.locationId),
    enabled: !!actions.locationId && (state?.joeActive ?? false),
    staleTime: 30_000,
  });
  const askJoeTo = useMemo(() => {
    const open = (joeTasks ?? [])
      .filter(
        (t) =>
          t.agentType === 'community' &&
          t.skillKey === 'community_scout' &&
          (t.state === 'awaiting_approval' || t.state === 'needs_review' || t.state === 'ready'),
      )
      .sort((a, b) => (b.lastAdvancedAt ?? 0) - (a.lastAdvancedAt ?? 0));
    return open[0] ? `/tasks?task=${encodeURIComponent(open[0].id)}` : '/tasks';
  }, [joeTasks]);
  const busy =
    actions.snooze.isPending ||
    actions.dismiss.isPending ||
    actions.plan.isPending ||
    actions.undo.isPending ||
    actions.settings.isPending ||
    actions.addReserve.isPending;
  const joeActive = state?.joeActive ?? false;
  const visiblePicks = useMemo(() => picks?.picks ?? [], [picks]);
  // "Plan a touch" on Joe's picks task lands here with ?plan=<pickId>: open that pick's planner once.
  const planParam = searchParams.get('plan');
  useEffect(() => {
    if (!planParam || !picks) return;
    const pick = picks.picks.find((p) => p.id === planParam);
    if (pick) setTarget({ kind: 'pick', pick });
    else toast.info('That pick is no longer open. Your current picks are below.');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('plan');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planParam, picks, setSearchParams]);
  const overdue = picks?.overdue?.[0] ?? null;

  const onSnooze = (pick: Pick) =>
    actions.snooze.mutate(pick.id, {
      onSuccess: (r) => setLearned({ message: r.message, ruleId: r.rule?.id ?? null }),
    });
  const onAddReserve = (pick: Pick) =>
    actions.addReserve.mutate(pick.id, { onSuccess: (r) => toast.success(r.message) });
  const onDismiss = (pick: Pick, reason: string) =>
    actions.dismiss.mutate(
      { pickId: pick.id, reason },
      {
        onSuccess: (r) => {
          setLearned({ message: r.message, ruleId: r.rule?.id ?? null });
          if (r.followUp) setFollowUp(r.followUp);
        },
      },
    );
  const onRefresh = () =>
    actions.refresh.mutate(undefined, {
      onSuccess: (r) => (r.queued ? toast.success(r.message) : toast.info(r.message)),
    });
  const onUndo = (ruleId: string) =>
    actions.undo.mutate(ruleId, { onSuccess: () => setLearned(null) });
  const answerFollowUp = () => {
    if (!state || !followUp) return;
    if (followUp.kind === 'drop_category' && followUp.category) {
      const category = followUp.category;
      const next = state.settings.enabledCategories.filter((c) => c !== category);
      actions.settings.mutate(
        { enabledCategories: next },
        {
          onSuccess: () =>
            toast.success(
              'Done. Joe will stop looking for that category; switch it back on from the Joe panel any time.',
            ),
        },
      );
    } else if (followUp.kind === 'shrink_radius') {
      const options = [...state.settings.radiusOptions].sort((a, b) => a - b);
      const smaller = [...options].reverse().find((r) => r < state.settings.radiusMi);
      if (smaller === undefined) toast.info('The radius is already at its smallest.');
      else
        actions.settings.mutate(
          { radiusMi: smaller },
          {
            onSuccess: () =>
              toast.success(`Radius set to ${smaller} mi. Joe will re-scan shortly.`),
          },
        );
    }
    setFollowUp(null);
  };
  const onSave = (input: PlanTouchInput) =>
    actions.plan.mutate(input, {
      onSuccess: (r) => {
        setTarget(null);
        const rhythm =
          state?.vocabulary.rhythms.find((x) => x.key === input.rhythm)?.label?.toLowerCase() ??
          'as planned';
        toast.success(
          r.draftRequested
            ? `Saved. Joe is drafting it; the draft will show in Tasks. Reminder: ${rhythm}.`
            : `Saved to your network. Reminder: ${rhythm}.`,
        );
        setParam('tab', 'network');
      },
    });
  const onRadius = (radiusMi: number) =>
    actions.settings.mutate(
      { radiusMi },
      { onSuccess: () => toast.success(`Radius set to ${radiusMi} mi. Joe will re-scan shortly.`) },
    );
  const onToggleCategory = (key: string) => {
    if (!state) return;
    const current = state.settings.enabledCategories;
    const next = current.includes(key) ? current.filter((c) => c !== key) : [...current, key];
    actions.settings.mutate(
      { enabledCategories: next },
      {
        onSuccess: () =>
          queryClient.invalidateQueries({ queryKey: communityKeys.state(actions.locationId) }),
      },
    );
  };

  const onToggleEventType = (key: string) => {
    if (!state) return;
    const current = state.settings.enabledEventTypes.map((e) => e.key);
    const next = current.includes(key) ? current.filter((t) => t !== key) : [...current, key];
    actions.settings.mutate(
      { enabledEventTypes: next },
      {
        onSuccess: () =>
          queryClient.invalidateQueries({ queryKey: communityKeys.state(actions.locationId) }),
      },
    );
  };

  const onToggleReferralCheckin = () => {
    if (!state) return;
    const next = !state.settings.referralCheckinOn;
    actions.settings.mutate(
      { referralCheckinOn: next },
      {
        onSuccess: () =>
          toast.success(
            next
              ? 'Joe will ask for referral counts each month.'
              : 'Monthly referral check-ins are off.',
          ),
      },
    );
  };

  if (stateError || (!stateLoading && !state)) {
    return (
      <div className={styles.page}>
        <EmptyState
          title={
            actions.locationId
              ? 'Community could not load.'
              : 'Choose a location to see its community.'
          }
          description={
            actions.locationId
              ? 'Your network and picks are safe. Try again in a moment.'
              : 'Community is kept per location.'
          }
          action={
            actions.locationId ? { label: 'Try again', onClick: () => refetchState() } : undefined
          }
        />
      </div>
    );
  }
  if (stateLoading || !state) {
    return (
      <div className={styles.page}>
        <Skeleton height={320} />
      </div>
    );
  }

  const kicker = `Community · ${state.settings.radiusMi} mi around ${[state.location.city, state.location.state].filter(Boolean).join(', ') || state.location.name}`;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Who should know your name this month?"
        subtitle={kicker}
        actions={
          <div className={styles.tabs} role="tablist" aria-label="Community views">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'picks'}
              className={clsx(styles.tab, tab === 'picks' && styles.tabActive)}
              onClick={() => setParam('tab', null)}
            >
              Joe's picks
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'network'}
              className={clsx(styles.tab, tab === 'network' && styles.tabActive)}
              onClick={() => setParam('tab', 'network')}
            >
              My network
            </button>
          </div>
        }
      />

      {followUp && (
        <div className={styles.learnedToast} role="status">
          <span>{followUp.detail}</span>
          <span className={styles.toastActions}>
            <button type="button" onClick={answerFollowUp}>
              {followUp.kind === 'drop_category' ? 'Yes, stop looking' : 'Yes, smaller radius'}
            </button>
            <button type="button" onClick={() => setFollowUp(null)}>
              Not now
            </button>
          </span>
        </div>
      )}
      {learned && (
        <div className={styles.learnedToast} role="status">
          <span>{learned.message}</span>
          {learned.ruleId && (
            <button type="button" onClick={() => onUndo(learned.ruleId!)}>
              Undo
            </button>
          )}
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.column}>
          {tab === 'picks' ? (
            !joeActive ? (
              <EmptyState
                title="Joe isn't assigned to your practice yet."
                description="Your network still works: add the people you already know under My network, log touches, and set the rhythm you want."
                action={{ label: 'Open my network', onClick: () => setParam('tab', 'network') }}
              />
            ) : (
              <>
                {overdue && (
                  <div className={styles.banner}>
                    <div>
                      <div className={styles.bannerTitle}>
                        {overdue.name} is {overdue.daysOverdue} days past your{' '}
                        {overdue.rhythmLabel.toLowerCase().replace('every ', '')} check-in
                      </div>
                      <div className={styles.bannerSub}>
                        Last touch:{' '}
                        {overdue.lastTouch
                          ? `${overdue.lastTouch.label}, ${new Date(overdue.lastTouch.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                          : 'none logged'}
                        .{' '}
                        {state.joePaused
                          ? 'Joe is paused, so this is on you for now.'
                          : 'Joe raises a reconnect suggestion in Tasks each morning while a partner is this far past due.'}
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        // The partner panel holds the "log a touch" form (a done touch clears the
                        // overdue state); the planner would only schedule a future one.
                        setParam('tab', 'network');
                        setParam('partner', overdue.id);
                      }}
                    >
                      Log a touch
                    </Button>
                  </div>
                )}
                <div className={styles.listHeader}>
                  <span className={styles.headerKicker}>
                    Joe's picks · {visiblePicks.length} this month
                  </span>
                  <span className={styles.listActions}>
                    <span className={styles.listMeta}>
                      {scanStatusLine(picks?.lastScan ?? null)}
                    </span>
                    {joeActive && !state.joePaused && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={
                          actions.refresh.isPending || scanInFlight(picks?.lastScan ?? null)
                        }
                        loading={actions.refresh.isPending}
                        onClick={onRefresh}
                      >
                        {scanInFlight(picks?.lastScan ?? null) ? 'Scanning…' : 'Refresh picks'}
                      </Button>
                    )}
                  </span>
                </div>
                {!picksLoading &&
                  visiblePicks.length > 0 &&
                  picks?.lastScan?.status === 'done' &&
                  picks.lastScan.note && (
                    <div className={styles.scanNote}>{picks.lastScan.note}</div>
                  )}
                {!picksLoading && picks?.sourceWarning && (
                  <div className={styles.scanNote}>{picks.sourceWarning}</div>
                )}
                {picksLoading ? (
                  <Skeleton height={200} />
                ) : visiblePicks.length === 0 ? (
                  <div className={styles.emptyPanel}>
                    {picksError
                      ? 'The picks could not be loaded. Try again in a moment.'
                      : emptyPicksLine(picks?.lastScan ?? null)}
                    {/address/i.test(picks?.lastScan?.note ?? '') && (
                      <>
                        {' '}
                        <Link to="/settings/practice">Add the address</Link>
                      </>
                    )}
                  </div>
                ) : (
                  visiblePicks.map((pick) => (
                    <PickCard
                      key={pick.id}
                      pick={pick}
                      reasons={state.vocabulary.reasons}
                      busy={busy}
                      onPlan={(p) => setTarget({ kind: 'pick', pick: p })}
                      onSnooze={onSnooze}
                      onDismiss={onDismiss}
                    />
                  ))
                )}
                {!picksLoading && (
                  <ReservePicks picks={picks?.reserve ?? []} busy={busy} onAdd={onAddReserve} />
                )}
              </>
            )
          ) : networkLoading ? (
            <Skeleton height={240} />
          ) : (
            <NetworkTable
              rows={network ?? []}
              selectedId={selectedPartner}
              onSelect={(row: PartnerRow) => setParam('partner', row.id)}
              onAdd={() => setTarget({ kind: 'new' })}
            />
          )}
        </div>

        {joeActive && (
          <JoePanel
            state={state}
            learned={picks?.learned ?? []}
            comingUp={picks?.comingUp ?? []}
            askJoeTo={askJoeTo}
            busy={busy}
            onRadius={onRadius}
            onToggleCategory={onToggleCategory}
            onToggleEventType={onToggleEventType}
            onToggleReferralCheckin={onToggleReferralCheckin}
            onUndo={onUndo}
          />
        )}
      </div>

      <PlanTouchModal
        target={target}
        vocabulary={state.vocabulary}
        joeActive={joeActive && !state.joePaused}
        categories={state.settings.categoryOptions}
        busy={actions.plan.isPending}
        onClose={() => setTarget(null)}
        onSave={onSave}
      />
      <PartnerDetail
        partnerId={selectedPartner}
        vocabulary={state.vocabulary}
        onClose={() => setParam('partner', null)}
        onPlan={(partner) => {
          setParam('partner', null);
          setTarget({ kind: 'partner', partner });
        }}
      />
    </div>
  );
}

type LastScan = PicksResponse['lastScan'];

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

/** The latest scan attempt in a few words, whatever its outcome; never a bare "No scan yet" after one ran. */
function scanStatusLine(scan: LastScan): string {
  if (!scan) return 'No scan yet';
  switch (scan.status) {
    case 'running':
      return 'Joe is scanning now';
    case 'awaiting_joe':
      return "Joe is writing this month's picks";
    case 'failed':
      return `Last scan ${shortDate(scan.at)} did not finish`;
    case 'skipped':
      return `Last scan ${shortDate(scan.at)} was skipped`;
    default:
      return `Refreshed ${shortDate(scan.at)}`;
  }
}

/** A scan still fetching: another one now would only queue behind it. "Awaiting Joe" does not
 *  count, since it can stand for a long time and refreshing is the way past it. */
function scanInFlight(scan: LastScan): boolean {
  return scan?.status === 'running';
}

/** Why the list is empty: the scan's own note when it has one, else the caught-up line. */
function emptyPicksLine(scan: LastScan): string {
  if (!scan) return 'Joe has not scanned yet. Your first picks arrive after his first scan.';
  if (scan.status === 'running' || scan.status === 'awaiting_joe')
    return "Joe is working on this month's picks. They will appear here shortly.";
  if (scan.note) return scan.note;
  return "You're caught up. Joe brings new picks on the first business day of each month, or sooner when you refresh.";
}
